<template>
  <div class="checkpoint-microlearning-tabs">
    <!-- Shared quiz-mode toolbar + (article-only) skip banners. -->
    <div v-if="tabIndex === 1 || tabIndex === 2" class="checkpoint-microlearning-tabs__quiz-mode">
      <div
        v-if="showQuizModeQuizActions || showQuizModeInspectionActions"
        class="checkpoint-microlearning-tabs__quiz-mode-row"
      >
        <div class="checkpoint-microlearning-tabs__quiz-mode-actions">
          <template v-if="showQuizModeQuizActions">
            <EXTooltip
              v-if="isQuizAtQuestionLimit"
              :title="QUIZ_MAX_QUESTIONS_TOOLTIP"
              placement="top"
              :delay="200"
            >
              <span class="checkpoint-microlearning-tabs__disabled-btn-tooltip-wrap">
                <StandardButton
                  type="magenta"
                  variant="secondary"
                  size="small"
                  label="Add Question"
                  left-icon="plusLeft"
                  disabled
                />
              </span>
            </EXTooltip>
            <StandardButton
              v-else
              type="magenta"
              variant="secondary"
              size="small"
              label="Add Question"
              left-icon="plusLeft"
              @click="onQuizModeAddQuestionClick"
            />
            <AddORAskIQButton
              :show-add-button="false"
              :show-a-i-button="true"
              label="Suggest"
              emp-label="Add"
              @addAI="emit('suggest-quiz')"
            />
            <MenuIcon
              class="checkpoint-microlearning-tabs__quiz-mode-menu"
              :vertical="true"
              :dropdown-items="quizRowBulkMenuItems"
              :show-tooltip="false"
              :width="240"
              @onClick="onQuizRowBulkMenuClick"
            />
          </template>
          <template v-else-if="showQuizModeInspectionActions">
            <EXTooltip
              v-if="isInspectionAtStepLimit"
              :title="INSPECTION_MAX_STEPS_TOOLTIP"
              placement="top"
              :delay="200"
            >
              <span class="checkpoint-microlearning-tabs__disabled-btn-tooltip-wrap">
                <StandardButton
                  type="magenta"
                  variant="secondary"
                  size="small"
                  label="Add Step"
                  left-icon="plusLeft"
                  disabled
                />
              </span>
            </EXTooltip>
            <StandardButton
              v-else
              type="magenta"
              variant="secondary"
              size="small"
              label="Add Step"
              left-icon="plusLeft"
              @click="onQuizModeAddStepClick"
            />
            <AddORAskIQButton
              :show-add-button="false"
              :show-a-i-button="true"
              label="Suggest"
              emp-label="Add"
              @addAI="emit('suggest-inspection')"
            />
            <MenuIcon
              class="checkpoint-microlearning-tabs__quiz-mode-menu"
              :vertical="true"
              :dropdown-items="inspectionRowBulkMenuItems"
              :show-tooltip="false"
              :width="240"
              @onClick="onInspectionRowBulkMenuClick"
            />
          </template>
        </div>
      </div>

      <template v-if="showSkipBanners">
        <div
          v-if="quizMode === 'BOTH_REQUIRED'"
          class="checkpoint-microlearning-tabs__skip-banner checkpoint-microlearning-tabs__skip-banner--both"
          role="status"
        >
          <span class="checkpoint-microlearning-tabs__skip-banner-icon" aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 6.667V10M10 13.333h.008M18.333 10A8.333 8.333 0 1 1 1.667 10a8.333 8.333 0 0 1 16.666 0Z"
                stroke="#667085"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <div class="checkpoint-microlearning-tabs__skip-banner-copy">
            <p class="checkpoint-microlearning-tabs__skip-banner-text">
              Employee must pass the quiz and pass the inspection to complete this checkpoint.
            </p>
          </div>
        </div>

        <div
          v-else-if="quizMode === 'SKIP_INSPECTION'"
          class="checkpoint-microlearning-tabs__skip-banner checkpoint-microlearning-tabs__skip-banner--inspection"
          role="status"
        >
          <span class="checkpoint-microlearning-tabs__skip-banner-icon" aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 6.667V10M10 13.333h.008M18.333 10A8.333 8.333 0 1 1 1.667 10a8.333 8.333 0 0 1 16.666 0Z"
                stroke="#7A5AF8"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <div class="checkpoint-microlearning-tabs__skip-banner-copy">
            <p class="checkpoint-microlearning-tabs__skip-banner-title">
              Passing this quiz completes the checkpoint
            </p>
            <p class="checkpoint-microlearning-tabs__skip-banner-text">
              Inspection steps are hidden and won't be published. Switch back to Quiz and Inspection
              to use them again.
            </p>
          </div>
        </div>

        <div
          v-else-if="quizMode === 'SKIP_QUIZ'"
          class="checkpoint-microlearning-tabs__skip-banner checkpoint-microlearning-tabs__skip-banner--quiz"
          role="status"
        >
          <span class="checkpoint-microlearning-tabs__skip-banner-icon" aria-hidden="true">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.858 8.858a2.5 2.5 0 0 0 3.536 3.536M11.763 5.37A7.5 7.5 0 0 1 17.5 10c-.74 1.167-1.66 2.167-2.708 2.958M4.583 4.583 15.417 15.417M8.333 5.392A7.5 7.5 0 0 0 2.5 10c.74 1.167 1.66 2.167 2.708 2.958a7.5 7.5 0 0 0 5.125 1.65"
                stroke="#667085"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <div class="checkpoint-microlearning-tabs__skip-banner-copy">
            <p class="checkpoint-microlearning-tabs__skip-banner-title">
              Quiz is skipped - inspection alone completes the checkpoint
            </p>
            <p class="checkpoint-microlearning-tabs__skip-banner-text">
              {{ skipQuizBannerBody }}
            </p>
          </div>
        </div>
      </template>
    </div>

    <!-- Quiz tab. -->
    <div v-if="tabIndex === 1" class="checkpoint-microlearning-tabs__article-quiz">
      <div
        v-if="quizMode === 'SKIP_QUIZ'"
        class="checkpoint-microlearning-tabs__skipped-empty"
        role="status"
      >
        <p class="checkpoint-microlearning-tabs__skipped-empty-title">Quiz skipped</p>
        <p class="checkpoint-microlearning-tabs__skipped-empty-text">
          Quiz questions were skipped for this checkpoint. Inspectors will use the checkpoint
          criteria and inspection steps to determine completion. You can add quiz questions at any
          time while editing.
        </p>
      </div>
      <template v-else>
        <CheckpointIQGenerationStatus
          v-if="isVideo && videoQuizGenerationStatus"
          class="checkpoint-microlearning-tabs__iq-thinking-banner"
          :status="videoQuizGenerationStatus"
          :trace-entries="inlineIqTraceEntries"
          :in-progress="isVideoQuizGenerationInProgress"
          :activity-label="videoIqGenerationActivityLabel"
          :show-status-pills="false"
          @retry="emit('quiz-suggest-retry')"
        />
        <IQThinkingBanner
          v-else-if="quizSuggestBannerState"
          class="checkpoint-microlearning-tabs__iq-thinking-banner"
          variant="quiz"
          :state="quizSuggestBannerState"
          @retry="emit('quiz-suggest-retry')"
        />
        <SuggestionUndoBanner
          v-if="quizUndoVisible"
          class="checkpoint-microlearning-tabs__undo-banner"
          title="IQ suggested these questions"
          message="The previous questions were replaced. Undo is available until you close this banner or leave the page."
          :busy="quizUndoBusy"
          @undo="emit('quiz-undo-suggestions')"
          @dismiss="emit('quiz-dismiss-undo')"
        />
        <QuizEditor
          v-if="showQuizEditor"
          ref="quizEditorRef"
          :quiz="proposedQuizPayload"
          :questions="proposedQuizQuestions.length ? proposedQuizQuestions : null"
          :quiz-mode="quizMode"
          :readonly="criteriaReadonly"
          hide-toolbar
          @add-question="emit('add-question', $event)"
          @delete-question="emit('delete-question', $event)"
          @update-question="emit('update-question', $event)"
          @reorder-questions="emit('reorder-questions', $event)"
          @quiz-bulk-action="emit('quiz-bulk-action', $event)"
          @suggest="emit('suggest-quiz')"
        />
      </template>
    </div>

    <!-- Inspection tab. -->
    <div v-if="tabIndex === 2" class="checkpoint-microlearning-tabs__article-inspection">
      <div
        v-if="quizMode === 'SKIP_INSPECTION'"
        class="checkpoint-microlearning-tabs__skipped-empty"
        role="status"
      >
        <p class="checkpoint-microlearning-tabs__skipped-empty-title">Inspection skipped</p>
        <p class="checkpoint-microlearning-tabs__skipped-empty-text">
          Inspection steps were skipped for this checkpoint. Inspectors will use the checkpoint
          criteria only to determine completion. You can add inspection steps at any time while
          editing.
        </p>
      </div>
      <template v-else>
        <CheckpointIQGenerationStatus
          v-if="isVideo && videoInspectionGenerationStatus"
          class="checkpoint-microlearning-tabs__iq-thinking-banner"
          :status="videoInspectionGenerationStatus"
          :trace-entries="inlineIqTraceEntries"
          :in-progress="isVideoInspectionGenerationInProgress"
          :activity-label="videoIqGenerationActivityLabel"
          :show-status-pills="false"
          @retry="emit('inspection-suggest-retry')"
        />
        <IQThinkingBanner
          v-else-if="inspectionSuggestBannerState"
          class="checkpoint-microlearning-tabs__iq-thinking-banner"
          variant="inspection"
          :state="inspectionSuggestBannerState"
          @retry="emit('inspection-suggest-retry')"
        />
        <InspectionEditor
          v-if="showInspectionEditor"
          ref="inspectionEditorRef"
          :protocol="proposedInspectionProtocolParsed"
          :steps="proposedInspectionSteps.length ? proposedInspectionSteps : null"
          :confirm-every-step="proposedInspectionConfirmEveryStep"
          :readonly="criteriaReadonly"
          :video-url="videoUrl"
          :suggest-disabled="inspectionSuggestDisabled"
          hide-toolbar
          @add-step="emit('add-step', $event)"
          @update-step="emit('update-step', $event)"
          @delete-step="emit('delete-step', $event)"
          @update:confirmEveryStep="emit('update:confirm-every-step', $event)"
          @delete-protocol-requested="emit('delete-protocol-requested', $event)"
          @timestamp-click="emit('timestamp-click', $event)"
          @suggest="emit('suggest-inspection')"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import AddORAskIQButton from "@components/AddORAskIQButton/AddORAskIQButton.vue";
import CheckpointIQGenerationStatus from "@components/CheckpointIQGenerationStatus/CheckpointIQGenerationStatus.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import InspectionEditor from "@components/InspectionEditor/InspectionEditor.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import QuizEditor from "@components/QuizEditor/QuizEditor.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import SuggestionUndoBanner from "@components/SuggestionUndoBanner/SuggestionUndoBanner.vue";

defineProps({
  /** Active content tab: 0 = video/article body, 1 = quiz, 2 = inspection. */
  tabIndex: {
    type: Number,
    default: 0,
  },
  /** resolvedQuizMode: BOTH_REQUIRED | SKIP_INSPECTION | SKIP_QUIZ. */
  quizMode: {
    type: String,
    default: "BOTH_REQUIRED",
  },
  showQuizModeQuizActions: {
    type: Boolean,
    default: false,
  },
  showQuizModeInspectionActions: {
    type: Boolean,
    default: false,
  },
  isQuizAtQuestionLimit: {
    type: Boolean,
    default: false,
  },
  isInspectionAtStepLimit: {
    type: Boolean,
    default: false,
  },
  quizRowBulkMenuItems: {
    type: Array,
    default: () => [],
  },
  inspectionRowBulkMenuItems: {
    type: Array,
    default: () => [],
  },
  /** Video-only (true) vs article (false) — gates IQ generation status and video inspection. */
  isVideo: {
    type: Boolean,
    default: false,
  },
  /** Skip banners are article-only today; video checkpoints never render them. */
  showSkipBanners: {
    type: Boolean,
    default: false,
  },
  skipQuizBannerBody: {
    type: String,
    default: "",
  },
  videoQuizGenerationStatus: {
    type: String,
    default: null,
  },
  isVideoQuizGenerationInProgress: {
    type: Boolean,
    default: false,
  },
  inlineIqTraceEntries: {
    type: Array,
    default: () => [],
  },
  videoIqGenerationActivityLabel: {
    type: String,
    default: "",
  },
  quizSuggestBannerState: {
    type: String,
    default: null,
  },
  /** ER-12788 — shows the "IQ suggested these questions" undo banner on the Quiz tab. */
  quizUndoVisible: {
    type: Boolean,
    default: false,
  },
  /** ER-12788 — the Undo Suggestions request is in flight. */
  quizUndoBusy: {
    type: Boolean,
    default: false,
  },
  showQuizEditor: {
    type: Boolean,
    default: false,
  },
  proposedQuizPayload: {
    type: Object,
    default: null,
  },
  proposedQuizQuestions: {
    type: Array,
    default: () => [],
  },
  criteriaReadonly: {
    type: Boolean,
    default: true,
  },
  videoInspectionGenerationStatus: {
    type: String,
    default: null,
  },
  isVideoInspectionGenerationInProgress: {
    type: Boolean,
    default: false,
  },
  inspectionSuggestBannerState: {
    type: String,
    default: null,
  },
  showInspectionEditor: {
    type: Boolean,
    default: false,
  },
  proposedInspectionProtocolParsed: {
    type: Object,
    default: null,
  },
  proposedInspectionSteps: {
    type: Array,
    default: () => [],
  },
  proposedInspectionConfirmEveryStep: {
    type: Boolean,
    default: false,
  },
  inspectionSuggestDisabled: {
    type: Boolean,
    default: true,
  },
  /** Video-only; empty string for article (no video to seek). */
  videoUrl: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "suggest-quiz",
  "suggest-inspection",
  "quiz-bulk-action",
  "quiz-suggest-retry",
  "quiz-undo-suggestions",
  "quiz-dismiss-undo",
  "inspection-suggest-retry",
  "add-question",
  "delete-question",
  "update-question",
  "reorder-questions",
  "add-step",
  "update-step",
  "delete-step",
  "update:confirm-every-step",
  "delete-protocol-requested",
  "timestamp-click",
]);

const quizEditorRef = ref(null);
const inspectionEditorRef = ref(null);

const QUIZ_MAX_QUESTIONS_TOOLTIP = "You have reached the maximum number of questions";
const INSPECTION_MAX_STEPS_TOOLTIP =
  "You have reached the maximum number of steps allowed in an inspection protocol";

function onQuizModeAddQuestionClick() {
  quizEditorRef.value?.openAddQuestionModal?.();
}

function onQuizModeAddStepClick() {
  inspectionEditorRef.value?.openAddStepModal?.();
}

function onQuizRowBulkMenuClick(item) {
  if (item?.value !== "delete-quiz") return;
  emit("quiz-bulk-action", { action: "delete-quiz" });
}

function onInspectionRowBulkMenuClick(item) {
  inspectionEditorRef.value?.onProtocolBulkMenuClick?.(item);
}
</script>

<style scoped>
.checkpoint-microlearning-tabs {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
}

.checkpoint-microlearning-tabs__quiz-mode {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.checkpoint-microlearning-tabs__quiz-mode-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.checkpoint-microlearning-tabs__quiz-mode-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
}

.checkpoint-microlearning-tabs__disabled-btn-tooltip-wrap {
  display: inline-flex;
}

.checkpoint-microlearning-tabs__skip-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  box-sizing: border-box;
}

.checkpoint-microlearning-tabs__skip-banner--both {
  background: #f9fafb;
  align-items: center;
}

.checkpoint-microlearning-tabs__skip-banner--inspection {
  background: #f4f3ff;
}

.checkpoint-microlearning-tabs__skip-banner--quiz {
  background: #f9fafb;
}

.checkpoint-microlearning-tabs__skip-banner-icon {
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
}

.checkpoint-microlearning-tabs__skip-banner-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.checkpoint-microlearning-tabs__skip-banner-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.checkpoint-microlearning-tabs__skip-banner--inspection
  .checkpoint-microlearning-tabs__skip-banner-title {
  color: #5925dc;
}

.checkpoint-microlearning-tabs__skip-banner--quiz
  .checkpoint-microlearning-tabs__skip-banner-title {
  color: #344054;
}

.checkpoint-microlearning-tabs__skip-banner-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.checkpoint-microlearning-tabs__skip-banner--both .checkpoint-microlearning-tabs__skip-banner-text {
  color: #475467;
}

.checkpoint-microlearning-tabs__skip-banner--inspection
  .checkpoint-microlearning-tabs__skip-banner-text {
  color: #6938ef;
}

.checkpoint-microlearning-tabs__skip-banner--quiz .checkpoint-microlearning-tabs__skip-banner-text {
  color: #667085;
}

.checkpoint-microlearning-tabs__article-quiz,
.checkpoint-microlearning-tabs__article-inspection {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.checkpoint-microlearning-tabs__skipped-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  padding: 32px 8px 24px;
  max-width: 640px;
  text-align: center;
}

.checkpoint-microlearning-tabs__skipped-empty-title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.checkpoint-microlearning-tabs__skipped-empty-text {
  margin: 0;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.checkpoint-microlearning-tabs__iq-thinking-banner {
  width: 100%;
}

.checkpoint-microlearning-tabs__undo-banner {
  width: 100%;
  margin-bottom: 16px;
}
</style>
